import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { getMaternityCenterUserList } from '@/service/user'

// ==================== 初始状态 ====================
const defaultState = {
  fetching: false,
  error: null,
  query: {},
  data: [],
  TECHNICIAN: { data: [], query: {}, fetching: false },
  NURSE: { data: [], query: {}, fetching: false }
}

// ==================== 数据处理函数 ====================
const processSingleDataType = ({ data, dataType }) => {
  if (!Array.isArray(data)) return []
  if (!dataType || dataType === 'default') return data

  switch (dataType) {
    case 'option':
      return data.map((item) => ({
        id: item.value,
        value: item.label
      }))
    default:
      return data
  }
}

// ==================== 服务类型映射配置 ====================
const switchPayload = (serviceType, query) => {
  const payload = {
    TECHNICIAN: () => getMaternityCenterUserList({ ...query, role: 'TECHNICIAN' }),
    NURSE: () => getMaternityCenterUserList({ ...query, role: 'NURSE' }),
    undefined: () => console.error('Redux: fetchMaternityCenterUserList 请求未定义 serviceType 类型')
  }
  return payload[serviceType] || payload.undefined
}

// ==================== 异步 Actions ====================
export const fetchMaternityCenterUserList = createAsyncThunk(
  'maternityCenterUser/fetchMaternityCenterUserList',
  async ({ serviceType, query = {}, dataType }, { rejectWithValue }) => {
    try {
      if (!serviceType) {
        return rejectWithValue('serviceType 参数是必需的')
      }
      const response = await switchPayload(serviceType, query)()
      return {
        data: response.data,
        serviceType,
        query,
        dataType
      }
    } catch (error) {
      return rejectWithValue(error.response?.data || `获取${serviceType}用户列表失败`)
    }
  }
)

// ==================== Slice 定义 ====================
const maternityCenterUserSlice = createSlice({
  name: 'maternityCenterUser',
  initialState: defaultState,
  reducers: {
    resetMaternityCenterUserState: (state, action) => {
      const serviceType = action.payload?.serviceType
      if (serviceType && state[serviceType]) {
        state[serviceType] = { data: [], query: {}, fetching: false }
      } else {
        state.query = {}
        state.data = []
        Object.keys(defaultState).forEach((key) => {
          if (!['fetching', 'error', 'query', 'data'].includes(key)) {
            state[key] = { data: [], query: {}, fetching: false }
          }
        })
      }
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchMaternityCenterUserList.pending, (state, action) => {
        const serviceType = action.meta.arg?.serviceType
        state.error = null
        if (serviceType && state[serviceType]) {
          state[serviceType].fetching = true
          state[serviceType].query = action.meta.arg.query || {}
        } else {
          state.fetching = true
          state.query = action.meta.arg?.query || {}
        }
      })
      .addCase(fetchMaternityCenterUserList.fulfilled, (state, action) => {
        const { data, serviceType, query, dataType } = action.payload
        const processedData = processSingleDataType({ data, dataType })
        if (serviceType && state[serviceType]) {
          state[serviceType].fetching = false
          state[serviceType].data = processedData
          state[serviceType].query = query || {}
        } else {
          state.fetching = false
          state.data = processedData
          state.query = query || {}
        }
        state.error = null
      })
      .addCase(fetchMaternityCenterUserList.rejected, (state, action) => {
        const serviceType = action.meta.arg?.serviceType
        if (serviceType && state[serviceType]) {
          state[serviceType].fetching = false
        } else {
          state.fetching = false
          state.data = []
        }
        state.error = action.payload
      })
  }
})

// ==================== 导出 ====================
export const { resetMaternityCenterUserState } = maternityCenterUserSlice.actions
export default maternityCenterUserSlice.reducer
